import Handsontable from 'handsontable';

function escapeHtml(text) {
    const s = String(text ?? '');
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * 셀 값 → 배지 또는 일반 텍스트 분류 (구버전 이모지 문자열 호환)
 */
export function classifyHandsontableStatus(raw) {
    const s = raw == null ? '' : String(raw);
    const trimmed = s.trim();
    if (!trimmed) return { kind: 'empty' };
    if (s.includes('loading-spinner')) return { kind: 'html', html: s };

    if (trimmed === '성공' || trimmed === '✅ 성공') {
        return { kind: 'pill', variant: 'success', label: '성공' };
    }
    if (trimmed === '완료' || trimmed === '✅ 완료') {
        return { kind: 'pill', variant: 'success', label: '완료' };
    }
    if (trimmed === '값 없음' || trimmed === '✅ 값 없음') {
        return { kind: 'pill', variant: 'neutral', label: '값 없음' };
    }
    if (trimmed === '실패' || trimmed === '❌ 실패') {
        return { kind: 'pill', variant: 'danger', label: '실패' };
    }
    if (trimmed.startsWith('❌')) {
        const body = trimmed.replace(/^❌\s*/, '').trim() || '오류';
        if (body.includes('\n')) {
            return { kind: 'pill-multiline', variant: 'danger', label: body };
        }
        return { kind: 'pill', variant: 'danger', label: body };
    }
    return { kind: 'text' };
}

function pillHtml(variant, label) {
    return `<span class="hot-status-pill hot-status-pill--${variant}"><span class="hot-status-pill__dot" aria-hidden="true"></span>${escapeHtml(label)}</span>`;
}

/** 메시지 + URL 등 줄바꿈 오류: 한글은 어절 단위, URL은 break-all */
function multilineDangerStackHtml(body) {
    const segments = String(body)
        .split(/\n/)
        .map(s => s.trim())
        .filter(Boolean);
    if (segments.length === 0) {
        return pillHtml('danger', '오류');
    }
    const [head, ...rest] = segments;
    const detailHtml = rest.length
        ? `<span class="hot-status-pill__detail">${rest.map(escapeHtml).join('<br/>')}</span>`
        : '';
    return `<span class="hot-status-pill hot-status-pill--danger hot-status-pill--multiline hot-status-pill--stack"><span class="hot-status-pill__top"><span class="hot-status-pill__dot" aria-hidden="true"></span><span class="hot-status-pill__msg">${escapeHtml(head)}</span></span>${detailHtml}</span>`;
}

/** 셀 안에서 배지·스피너를 가로·세로 가운데 + 너비 제한(줄바꿈) */
function wrapStatusCellInner(html) {
    return `<div class="hot-status-cell-inner">${html}</div>`;
}

/**
 * Handsontable 커스텀 렌더러: 상태 열에만 사용 (스피너 HTML / 배지 / 그 외 텍스트)
 */
export function hotStatusPillRenderer(instance, td, row, col, prop, value, cellProperties) {
    Handsontable.dom.empty(td);
    td.classList.remove('hot-status-cell--multiline', 'hot-status-td-center');

    const classified = classifyHandsontableStatus(value);

    if (classified.kind === 'html') {
        td.innerHTML = wrapStatusCellInner(classified.html);
        td.classList.add('hot-status-td-center');
        return td;
    }
    if (classified.kind === 'pill') {
        td.innerHTML = wrapStatusCellInner(pillHtml(classified.variant, classified.label));
        td.classList.add('hot-status-td-center');
        return td;
    }
    if (classified.kind === 'pill-multiline') {
        td.classList.add('hot-status-cell--multiline', 'hot-status-td-center');
        td.innerHTML = wrapStatusCellInner(multilineDangerStackHtml(classified.label));
        return td;
    }

    const textR = Handsontable.renderers?.getRenderer?.('text');
    if (textR) {
        textR(instance, td, row, col, prop, value, cellProperties);
    } else {
        td.textContent = value == null ? '' : String(value);
    }
    return td;
}

/**
 * 엑셀 등보내기용: 태그·배지 없이 읽을 문자열만
 */
export function plainStatusForExport(value) {
    const classified = classifyHandsontableStatus(value);
    if (classified.kind === 'html') return '';
    if (classified.kind === 'pill' || classified.kind === 'pill-multiline') {
        return classified.label.replace(/\n/g, ' ');
    }
    if (classified.kind === 'empty') return '';
    const s = value == null ? '' : String(value);
    return s.replace(/<[^>]*>/g, '').trim();
}
